import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

interface ToastItem {
  id: number;
  message: string;
  action?: { label: string; run: () => void };
}

interface ToastApi {
  show: (message: string, action?: ToastItem['action']) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const show = useCallback<ToastApi['show']>((message, action) => {
    const id = ++seq.current;
    setItems((prev) => [...prev.slice(-2), { id, message, action }]);
    window.setTimeout(() => setItems((prev) => prev.filter((i) => i.id !== id)), action ? 6000 : 3500);
  }, []);
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className="toast">
            <span className="spacer">{i.message}</span>
            {i.action && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  i.action!.run();
                  setItems((prev) => prev.filter((x) => x.id !== i.id));
                }}
              >
                {i.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
